import { Button, Group, Stack, Text } from '@mantine/core';
import { ArrowRight, ArrowsClockwise, ClipboardText } from '@phosphor-icons/react';
import { useEffect, useMemo, useState, type FC } from 'react';

import { getB2bPoOverview, type B2bPoOverview } from '@api/b2bPo';
import {
    getChannelHealth,
    type AvailableChannel, type DistributionChannelHealth, type DistributionPlanSummary,
} from '@api/distribution';
import SectionHeader from '@components/common/SectionHeader';
import DataSheet, { type DataSheetColumn } from '@components/ui/DataSheet';
import { neutralColors, semanticColors } from '@styles/design-system';

interface PoRow {
    channel: AvailableChannel;
    channelName: string;
    openPoCount: number;
    weightedDohDays: number;
    targetDohDays: number;
    hasHealth: boolean;
}

interface Props {
    poChannels: AvailableChannel[];
    poChannelKeys: Set<string>;
    draftPlans: DistributionPlanSummary[];
    draftPlansLoading: boolean;
    onChannelClick: (channel: AvailableChannel) => void;
    onComputePlan: () => void;
    onShowAllPlans: () => void;
    onViewPlan: (plan: DistributionPlanSummary) => void;
}

const PLAN_STATUS_COLOR: Record<string, string> = {
    DRAFT: neutralColors.textMuted,
    APPROVED: semanticColors.info.primary,
    EXECUTING: semanticColors.info.primary,
    COMPLETED: semanticColors.positive.primary,
    ARCHIVED: neutralColors.textDisabled,
};

function buildColumns(onChannelClick: (ch: AvailableChannel) => void): DataSheetColumn<PoRow>[] {
    return [
        {
            key: 'channelName',
            header: 'Channel',
            width: 260,
            align: 'left',
            render: (_v, row) => (
                <button
                    type="button"
                    onClick={e => { e.stopPropagation(); onChannelClick(row.channel); }}
                    style={{
                        border: 'none', background: 'transparent', padding: 0, cursor: 'pointer',
                        fontSize: 13, fontWeight: 600, color: semanticColors.info.primary, textAlign: 'left',
                    }}
                >
                    {row.channelName}
                </button>
            ),
        },
        {
            key: 'openPoCount',
            header: 'Open POs',
            width: 120,
            align: 'right',
            render: (_v, row) => (
                <Text style={{ fontSize: 13, fontWeight: row.openPoCount > 0 ? 600 : 400, color: row.openPoCount > 0 ? neutralColors.textPrimary : neutralColors.textMuted }}>
                    {row.openPoCount}
                </Text>
            ),
        },
        {
            key: 'weightedDohDays',
            header: 'Avg DoH',
            width: 180,
            align: 'right',
            render: (_v, row) => {
                if (!row.hasHealth) return <Text style={{ fontSize: 13, color: neutralColors.textMuted }}>—</Text>;
                const doh = row.weightedDohDays;
                const target = row.targetDohDays;
                const pct = target > 0 ? Math.min(doh / target, 1) : 0;
                const color = doh < target * 0.5
                    ? semanticColors.critical.primary
                    : doh < target
                    ? semanticColors.warning.primary
                    : semanticColors.positive.primary;
                return (
                    <div style={{ textAlign: 'right' }}>
                        <Text style={{ fontSize: 13, fontWeight: 600, color }}>
                            {doh >= 999 ? '—' : `${Math.floor(doh)}d`}
                            <Text span style={{ fontSize: 11, fontWeight: 400, color: neutralColors.textMuted, marginLeft: 4 }}>
                                / {target}d
                            </Text>
                        </Text>
                        <div style={{ marginTop: 3, height: 3, borderRadius: 2, backgroundColor: neutralColors.surfaceHover, overflow: 'hidden' }}>
                            <div style={{ width: `${pct * 100}%`, height: '100%', backgroundColor: color, borderRadius: 2 }} />
                        </div>
                    </div>
                );
            },
        },
    ];
}

const PoDrivenOverview: FC<Props> = ({
    poChannels, poChannelKeys, draftPlans, draftPlansLoading,
    onChannelClick, onComputePlan, onShowAllPlans, onViewPlan,
}) => {
    const [health, setHealth] = useState<DistributionChannelHealth[]>([]);
    const [overview, setOverview] = useState<B2bPoOverview | null>(null);
    const [loading, setLoading] = useState(true);

    const refresh = (bust = false) => {
        setLoading(true);
        Promise.all([
            getChannelHealth({ bust }).then(d => d.channels.filter(c => poChannelKeys.has(c.channelAbbreviationKey))),
            getB2bPoOverview().catch(() => null),
        ])
            .then(([h, o]) => { setHealth(h); setOverview(o); })
            .catch(() => {})
            .finally(() => setLoading(false));
    };

    useEffect(() => { refresh(false); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const rows = useMemo<PoRow[]>(() => {
        const healthByKey = new Map(health.map(h => [h.channelAbbreviationKey, h]));
        const openByKey = new Map((overview?.channels ?? []).map(c => [c.channelAbbreviationKey, c.open]));
        return poChannels.map(ch => {
            const h = healthByKey.get(ch.channelAbbreviationKey);
            return {
                channel: ch,
                channelName: ch.channelName || ch.channelAbbreviationKey,
                openPoCount: openByKey.get(ch.channelAbbreviationKey) ?? 0,
                weightedDohDays: h?.weightedDohDays ?? 0,
                targetDohDays: h?.targetDohDays ?? 0,
                hasHealth: h !== undefined,
            };
        });
    }, [poChannels, health, overview]);

    if (poChannels.length === 0) {
        return (
            <Stack align="center" justify="center" style={{ paddingTop: 60 }} gap={8}>
                <ClipboardText size={32} color={neutralColors.textMuted} />
                <Text style={{ fontSize: 14, color: neutralColors.textMuted }}>
                    No channels are set to PO-driven yet.
                </Text>
                <Text style={{ fontSize: 12, color: neutralColors.textDisabled, maxWidth: 360, textAlign: 'center' }}>
                    Set a channel's fulfilment mode to “PO-driven” in Settings → Channels to manage its incoming POs here.
                </Text>
            </Stack>
        );
    }

    return (
        <Stack gap={24}>
            <div>
                <SectionHeader
                    title="PO-Driven Channels"
                    actions={
                        <Group gap={8} wrap="nowrap">
                            <Button variant="subtle" size="xs" fw={500} leftSection={<ArrowsClockwise size={14} />} onClick={() => refresh(true)} loading={loading} color="dark">
                                Refresh
                            </Button>
                            <Button variant="action" size="sm" rightSection={<ArrowRight size={16} />} onClick={onComputePlan}>
                                Compute Plan
                            </Button>
                        </Group>
                    }
                />
                <DataSheet
                    columns={buildColumns(onChannelClick)}
                    data={rows}
                    isLoading={loading}
                    hideHeaderBar
                    maxHeight="auto"
                    getRowKey={row => row.channel.channelAbbreviationKey}
                    onRowClick={row => onChannelClick(row.channel)}
                />
            </div>

            {(draftPlansLoading || draftPlans.length > 0) && (
                <div>
                    <SectionHeader
                        title="Continue Working on These Plans"
                        actions={
                            <Button variant="subtle" size="xs" fw={500} rightSection={<ArrowRight size={14} />} onClick={onShowAllPlans} color="dark">
                                Show all plans
                            </Button>
                        }
                    />
                    {draftPlansLoading ? (
                        <Stack gap={8}>
                            {Array.from({ length: 2 }).map((_, i) => (
                                <div key={i} style={{ height: 60, borderRadius: 12, backgroundColor: 'rgba(0,0,0,0.04)' }} />
                            ))}
                        </Stack>
                    ) : (
                        <Stack gap={8}>
                            {draftPlans.map(plan => (
                                <div
                                    key={plan.id}
                                    onClick={() => onViewPlan(plan)}
                                    style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '12px 16px', border: '1px solid rgba(1,149,255,0.2)',
                                        borderRadius: 12, backgroundColor: 'rgba(1,149,255,0.02)', cursor: 'pointer',
                                    }}
                                >
                                    <div style={{ minWidth: 0 }}>
                                        <Text style={{ fontSize: 14, fontWeight: 600, color: neutralColors.textPrimary }}>{plan.name}</Text>
                                        <Text style={{ fontSize: 12, color: neutralColors.textMuted, marginTop: 2 }}>
                                            {new Date(plan.planDate).toLocaleDateString()} · {plan.channelNames.join(', ')} · {plan.lineCount} line{plan.lineCount !== 1 ? 's' : ''}
                                        </Text>
                                    </div>
                                    <Group gap={8} style={{ flexShrink: 0 }}>
                                        <span style={{
                                            fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase',
                                            color: PLAN_STATUS_COLOR[plan.status] ?? neutralColors.textMuted,
                                            backgroundColor: `${PLAN_STATUS_COLOR[plan.status]}18`,
                                            borderRadius: 4, padding: '3px 8px',
                                        }}>
                                            {plan.status}
                                        </span>
                                        <ArrowRight size={16} color={neutralColors.textMuted} />
                                    </Group>
                                </div>
                            ))}
                        </Stack>
                    )}
                </div>
            )}
        </Stack>
    );
};

export default PoDrivenOverview;
